import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Users, Package, ShoppingCart, DollarSign,
  RefreshCw, AlertTriangle, Clock, Mail, Send, Eye, Settings2, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

const API_URL = '';

function MetricCard({ title, value, change, icon: Icon, prefix = '', suffix = '', loading }) {
  const isPositive = change >= 0;
  
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            {loading ? (
              <div className="h-8 w-24 bg-slate-200 animate-pulse rounded mt-2" />
            ) : (
              <p className="text-2xl font-bold text-slate-900 mt-1">
                {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
              </p>
            )}
            {change !== undefined && !loading && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{isPositive ? '+' : ''}{change.toFixed(1)}%</span>
                <span className="text-slate-400">vs prev period</span>
              </div>
            )}
          </div>
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Icon className="w-5 h-5 text-indigo-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RevenueChart({ data, loading }) {
  if (loading) {
    return (
      <Card className="col-span-2">
        <CardHeader>
          <CardTitle>Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 bg-slate-100 animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="col-span-2">
        <CardHeader>
          <CardTitle>Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-48 text-slate-400">
          No revenue data available
        </CardContent>
      </Card>
    );
  }

  // Simple bar chart using CSS
  const maxRevenue = Math.max(...data.map(d => d.revenue), 1);
  
  return (
    <Card className="col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-indigo-600" />
          Revenue Trend
        </CardTitle>
        <CardDescription>
          Total: €{data.reduce((sum, d) => sum + d.revenue, 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1 h-48">
          {data.slice(-30).map((item, idx) => {
            const height = (item.revenue / maxRevenue) * 100;
            const date = new Date(item.date);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            
            return (
              <div
                key={idx}
                className="flex-1 group relative"
              >
                <div
                  className={`w-full rounded-t transition-all ${isWeekend ? 'bg-indigo-300' : 'bg-indigo-500'} group-hover:bg-indigo-600`}
                  style={{ height: `${Math.max(height, 2)}%` }}
                />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                  <div className="bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                    {item.date}: €{item.revenue.toFixed(2)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-xs text-slate-400">
          <span>{data[0]?.date}</span>
          <span>{data[data.length - 1]?.date}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function PopularServicesCard({ services, loading }) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Popular Services</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-slate-100 animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5 text-indigo-600" />
          Popular Services
        </CardTitle>
        <CardDescription>By purchase count</CardDescription>
      </CardHeader>
      <CardContent>
        {services.length === 0 ? (
          <p className="text-slate-400 text-center py-4">No data available</p>
        ) : (
          <div className="space-y-3">
            {services.slice(0, 5).map((service, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-medium">
                    {idx + 1}
                  </span>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{service.name}</p>
                    <p className="text-xs text-slate-500">{service.purchases} purchases</p>
                  </div>
                </div>
                <span className="text-sm font-medium text-green-600">
                  €{service.total_revenue.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentOrdersCard({ orders, loading }) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-slate-100 animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-amber-100 text-amber-700',
      failed: 'bg-red-100 text-red-700',
      cancelled: 'bg-slate-100 text-slate-700'
    };
    return styles[status] || styles.pending;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-indigo-600" />
          Recent Orders
        </CardTitle>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <p className="text-slate-400 text-center py-4">No orders yet</p>
        ) : (
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {orders.map((order, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 border-b border-slate-100 last:border-0">
                <div>
                  <p className="font-medium text-slate-900 text-sm">{order.client_name}</p>
                  <p className="text-xs text-slate-500">
                    {order.items_count} item{order.items_count !== 1 ? 's' : ''} • {order.created_at?.split('T')[0]}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-slate-900">€{order.total.toFixed(2)}</p>
                  <Badge className={`text-xs ${getStatusBadge(order.status)}`}>
                    {order.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExpiringServicesCard({ data, loading }) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Expiring Services</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-24 bg-slate-100 animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-600" />
          Expiring Services
        </CardTitle>
        <CardDescription>Upcoming renewals</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">{data?.breakdown?.next_7_days || 0}</p>
            <p className="text-xs text-red-500">Next 7 days</p>
          </div>
          <div className="text-center p-2 bg-amber-50 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">{data?.breakdown?.next_14_days || 0}</p>
            <p className="text-xs text-amber-500">8-14 days</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-2xl font-bold text-slate-600">{data?.breakdown?.next_30_days || 0}</p>
            <p className="text-xs text-slate-500">15-30 days</p>
          </div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700">
              Potential renewal revenue: <strong>€{(data?.potential_renewal_revenue || 0).toLocaleString()}</strong>
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsDashboard({ tenantId, token }) {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [popularServices, setPopularServices] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [expiringServices, setExpiringServices] = useState(null);
  const [error, setError] = useState(null);
  
  // Email report states
  const [reportPreferences, setReportPreferences] = useState({
    weekly_enabled: true,
    monthly_enabled: true,
    email_enabled: true
  });
  const [sendingReport, setSendingReport] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const fetchAnalytics = async () => {
    if (!tenantId || !token) return;
    
    setLoading(true);
    setError(null);
    
    const headers = { 'Authorization': `Bearer ${token}` };
    
    try {
      const [overviewRes, revenueRes, servicesRes, ordersRes, expiringRes, prefsRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/${tenantId}/overview?days=${period}`, { headers }),
        fetch(`${API_URL}/api/analytics/${tenantId}/revenue?days=${period}`, { headers }),
        fetch(`${API_URL}/api/analytics/${tenantId}/popular-services`, { headers }),
        fetch(`${API_URL}/api/analytics/${tenantId}/recent-orders?limit=10`, { headers }),
        fetch(`${API_URL}/api/analytics/${tenantId}/expiring-services?days=30`, { headers }),
        fetch(`${API_URL}/api/analytics/reports/preferences`, { headers })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (revenueRes.ok) {
        const revenueData = await revenueRes.json();
        setRevenue(revenueData.data || []);
      }
      if (servicesRes.ok) {
        const servicesData = await servicesRes.json();
        setPopularServices(servicesData.services || []);
      }
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setRecentOrders(ordersData.orders || []);
      }
      if (expiringRes.ok) setExpiringServices(await expiringRes.json());
      if (prefsRes.ok) setReportPreferences(await prefsRes.json());
      
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updates) => {
    const newPrefs = { ...reportPreferences, ...updates };
    setReportPreferences(newPrefs);
    
    try {
      const res = await fetch(`${API_URL}/api/analytics/reports/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPrefs)
      });
      
      if (res.ok) {
        toast.success('Report preferences updated');
      } else {
        toast.error('Failed to update preferences');
      }
    } catch (err) {
      toast.error('Failed to update preferences');
    }
  };

  const sendReport = async (reportType) => {
    setSendingReport(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/reports/send/${reportType}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`${reportType === 'weekly' ? 'Weekly' : 'Monthly'} report sent to ${data.sent} admin(s)`);
      } else {
        toast.error(data.detail || 'Failed to send report');
      }
    } catch (err) {
      toast.error('Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  const previewReport = async (reportType) => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/reports/preview/${reportType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.html);
        setShowPreview(true);
      } else {
        toast.error('Failed to load preview');
      }
    } catch (err) {
      toast.error('Failed to load preview');
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, token, period]);

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">Failed to Load Analytics</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const metrics = overview?.metrics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Business Analytics</h3>
          <p className="text-sm text-slate-500">Monitor your portal's performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Revenue"
          value={metrics.revenue?.current || 0}
          change={metrics.revenue?.change_percent}
          icon={DollarSign}
          prefix="€"
          loading={loading}
        />
        <MetricCard
          title="New Clients"
          value={metrics.new_clients?.current || 0}
          change={metrics.new_clients?.change_percent}
          icon={Users}
          loading={loading}
        />
        <MetricCard
          title="Active Services"
          value={metrics.active_services || 0}
          icon={Package}
          loading={loading}
        />
        <MetricCard
          title="Avg Order Value"
          value={metrics.avg_order_value || 0}
          icon={ShoppingCart}
          prefix="€"
          loading={loading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RevenueChart data={revenue} loading={loading} />
        <ExpiringServicesCard data={expiringServices} loading={loading} />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PopularServicesCard services={popularServices} loading={loading} />
        <RecentOrdersCard orders={recentOrders} loading={loading} />
      </div>

      {/* Email Reports Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            Email Reports
          </CardTitle>
          <CardDescription>
            Receive automated analytics reports directly in your inbox
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Report Preferences */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-4">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                <Settings2 className="w-4 h-4" />
                Report Preferences
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <Label htmlFor="email-enabled" className="text-sm font-medium">Email Reports</Label>
                    <p className="text-xs text-slate-500">Enable email delivery</p>
                  </div>
                  <Switch
                    id="email-enabled"
                    checked={reportPreferences.email_enabled}
                    onCheckedChange={(checked) => updatePreferences({ email_enabled: checked })}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <Label htmlFor="weekly-enabled" className="text-sm font-medium">Weekly Reports</Label>
                    <p className="text-xs text-slate-500">Every Monday at 9 AM</p>
                  </div>
                  <Switch
                    id="weekly-enabled"
                    checked={reportPreferences.weekly_enabled}
                    onCheckedChange={(checked) => updatePreferences({ weekly_enabled: checked })}
                    disabled={!reportPreferences.email_enabled}
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div>
                    <Label htmlFor="monthly-enabled" className="text-sm font-medium">Monthly Reports</Label>
                    <p className="text-xs text-slate-500">1st of each month</p>
                  </div>
                  <Switch
                    id="monthly-enabled"
                    checked={reportPreferences.monthly_enabled}
                    onCheckedChange={(checked) => updatePreferences({ monthly_enabled: checked })}
                    disabled={!reportPreferences.email_enabled}
                  />
                </div>
              </div>
            </div>

            {/* Send Reports Now */}
            <div className="p-4 bg-indigo-50 rounded-lg">
              <h4 className="font-medium text-indigo-900 mb-3 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Send Reports Now
              </h4>
              <p className="text-sm text-indigo-700 mb-4">
                Send an analytics report immediately to all admin email addresses
              </p>
              
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => previewReport('weekly')}
                    className="border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Weekly
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => sendReport('weekly')}
                    disabled={sendingReport}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {sendingReport ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Weekly Report
                  </Button>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => previewReport('monthly')}
                    className="border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Monthly
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => sendReport('monthly')}
                    disabled={sendingReport}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {sendingReport ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Monthly Report
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Report Preview</DialogTitle>
            <DialogDescription>
              This is how the email report will look
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh] border rounded-lg">
            {previewHtml && (
              <iframe 
                srcDoc={previewHtml}
                title="Report Preview"
                className="w-full h-[600px] border-0"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
